import { LoadFailureClass } from './sheet-load-trace.util';

/**
 * How close together two failures of the SAME class have to land to be
 * treated as "the same incident" rather than two independent ones.
 *
 * Deliberately short: concurrent on-load API calls that all die to one root
 * cause (a network outage, a backend restart) fail within milliseconds of
 * each other — the browser rejects every in-flight request near-instantly
 * once it decides the connection is gone. A failure outside this window is
 * treated as a fresh, independent incident and toasts again.
 */
export const PAGE_LOAD_FAILURE_TOAST_DEDUPE_WINDOW_MS = 1_000;

/**
 * Caps a page's load-failure toasts to ONE per {@link LoadFailureClass}
 * within {@link PAGE_LOAD_FAILURE_TOAST_DEDUPE_WINDOW_MS}, so a page that
 * fires several API calls on load-up (e.g. User Management's listing + its
 * own-profile read) does not stack N identical "Connection lost…" toasts
 * when one root cause fails all of them at once.
 *
 * ## Why a time window, not a permanent per-class latch
 *
 * A permanent "has this class ever toasted" flag would also silence a
 * genuinely later, unrelated failure of the same class — e.g. the listing
 * fails again, independently, five minutes after the page settled. That is
 * new information the user is owed. The window only collapses failures that
 * land close enough together to plausibly share one cause; anything outside
 * it toasts fresh. Collapsing on EXACT TIMESTAMP equality would miss two
 * calls that fail a few milliseconds apart, so a half-open window keyed on
 * "most recent toast of this class" is used instead of a plain `Set`.
 *
 * ## Why only the TOAST is capped, never the classification
 *
 * `buildPageLoadFailureNotice` (and `SheetLoadTraceService.fail()`) always
 * `console.error`s unconditionally, regardless of whether this gate lets the
 * toast through — so capping the toast costs NOTHING in traceability. Every
 * one of the page's API calls still produces its own `[page-load] MBRS-...`
 * console line with its own scope and code; a developer with the machine in
 * hand can still tell three concurrent failures apart. Only the user-facing
 * stack of identical toasts is what this removes.
 *
 * Usage — one instance per component (page-local state, not a shared
 * service; each page's load-up cluster is independent of every other page's):
 *
 * ```ts
 * private readonly loadFailureToastGate = new PageLoadFailureToastGate();
 *
 * // ...in each API call's error handler:
 * const notice = buildPageLoadFailureNotice('USER_MGMT_LISTING', error);
 * if (notice.silent) {
 *   this.authFailureNotice.stage(notice.summary);
 * } else if (this.loadFailureToastGate.shouldToast(notice.failure)) {
 *   this.messageService.add({ key: 'user', severity: 'error', summary: notice.summary, life: DIAGNOSTIC_TOAST_LIFE_MS });
 * }
 * ```
 */
export class PageLoadFailureToastGate {
  private readonly lastShownAt = new Map<LoadFailureClass, number>();

  /**
   * @param windowMs     The dedupe window.
   * @param isToastingEnabled REFACTORED-DIAGNOSTIC-CODES-FLAG: pass
   *   `() => runtimeConfig.refactoredDiagnosticCodesEnabled()`. While it returns
   *   `false` (production) every page-level load failure is silent, as on
   *   `main` before the coded diagnostics. Defaults to always-on so a caller
   *   that does not care (specs) keeps today's behaviour. Remove with the flag.
   */
  constructor(
    private readonly windowMs: number = PAGE_LOAD_FAILURE_TOAST_DEDUPE_WINDOW_MS,
    private readonly isToastingEnabled: () => boolean = () => true,
  ) {}

  /**
   * True the first time `failure` is seen, or the first time past the
   * window since it was last shown. False means "already toasted this
   * incident — stay quiet", NOT "never toast this class again".
   */
  shouldToast(failure: LoadFailureClass): boolean {
    if (!this.isToastingEnabled()) return false; // REFACTORED-DIAGNOSTIC-CODES-FLAG
    const now = Date.now();
    const last = this.lastShownAt.get(failure);
    if (last !== undefined && now - last < this.windowMs) return false;

    this.lastShownAt.set(failure, now);
    return true;
  }
}
